import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Tag,
  Button,
  Input,
  Select,
  Tabs,
  Drawer,
  Space,
  Avatar,
  Modal,
  Form,
  Descriptions,
  Badge,
  Spin,
  message
} from 'antd';
import {
  EyeOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  ReloadOutlined,
  FileDoneOutlined,
  ClockCircleOutlined,
  CarOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  SafetyCertificateOutlined,
  UserOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

import {
  getAdminStudentVerifications,
  getStudentProfile,
  getIncompleteStudents,
  getCertificateDeliveryRequests,
  updateCertificateDeliveryRequest,
  exportAllStudentProfiles
} from '../../../Constants/Api/functions/function';

import './style.scss';

const { Option } = Select;
const { TabPane } = Tabs;

const StudentVerificationsAdmin = () => {
  const [activeTab, setActiveTab] = useState('submissions');
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Incomplete students state
  const [incompleteStudents, setIncompleteStudents] = useState([]);
  const [incompleteCount, setIncompleteCount] = useState(0);

  // Delivery requests state
  const [deliveryRequests, setDeliveryRequests] = useState([]);

  // Selected Student Details Drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [detailedData, setDetailedData] = useState(null);

  // Tracking Modal for Delivery
  const [trackingModalVisible, setTrackingModalVisible] = useState(false);
  const [selectedDeliveryReq, setSelectedDeliveryReq] = useState(null);
  const [deliveryTrackingNumber, setDeliveryTrackingNumber] = useState('');
  const [deliveryCourierCompany, setDeliveryCourierCompany] = useState('');
  const [deliveryStatus, setDeliveryStatus] = useState('pending');

  // Load Submissions
  const fetchSubmissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const res = await getAdminStudentVerifications(params);
      if (res && res.success) {
        setSubmissions(res.data || []);
      }
    } catch (err) {
      console.error('fetchSubmissions error:', err);
      message.error('Failed to load student verification submissions');
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchQuery]);

  // Load Incomplete Students
  const fetchIncompleteStudents = useCallback(async () => {
    try {
      const res = await getIncompleteStudents();
      if (res && res.success) {
        setIncompleteStudents(res.students || []);
        setIncompleteCount(res.count || 0);
      }
    } catch (err) {
      console.error('fetchIncompleteStudents error:', err);
    }
  }, []);

  // Load Delivery Requests
  const fetchDeliveryRequests = useCallback(async () => {
    try {
      const res = await getCertificateDeliveryRequests();
      if (res && res.success) {
        setDeliveryRequests(res.data || []);
      }
    } catch (err) {
      console.error('fetchDeliveryRequests error:', err);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
    fetchIncompleteStudents();
    fetchDeliveryRequests();
  }, [fetchSubmissions, fetchIncompleteStudents, fetchDeliveryRequests]);

  // Load Single Student Detailed Profile for Read-Only Inspection
  const handleOpenStudentDetails = async (userId) => {
    setDrawerVisible(true);
    try {
      setProfileLoading(true);
      const res = await getStudentProfile(userId);
      if (res && res.success) {
        setDetailedData(res);
      } else {
        message.error('Failed to load student details');
      }
    } catch (err) {
      console.error('handleOpenStudentDetails error:', err);
      message.error('Error fetching student profile details');
    } finally {
      setProfileLoading(false);
    }
  };

  // Delivery Request Status Update
  const handleSaveDeliveryTracking = async () => {
    if (!selectedDeliveryReq) return;
    try {
      const res = await updateCertificateDeliveryRequest(selectedDeliveryReq.id, {
        status: deliveryStatus,
        trackingNumber: deliveryTrackingNumber,
        courierCompany: deliveryCourierCompany,
      });

      if (res && res.success) {
        message.success('Delivery tracking updated successfully');
        setTrackingModalVisible(false);
        fetchDeliveryRequests();
      } else {
        message.error(res?.message || 'Failed to update delivery');
      }
    } catch (err) {
      console.error('handleSaveDeliveryTracking error:', err);
      message.error('Error updating delivery details');
    }
  };

  // Status Badge Helper
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
      case 'verified':
        return <Tag color="success" icon={<CheckCircleOutlined />}>Submitted</Tag>;
      case 'draft':
      default:
        return <Tag color="warning" icon={<ClockCircleOutlined />}>Draft / Incomplete</Tag>;
    }
  };

  // ─── Export All Student Details to Excel ───────────────────────────────────
  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      message.loading({ content: 'Fetching all student profile records...', key: 'exportMsg', duration: 0 });
      const res = await exportAllStudentProfiles();
      if (!res || !res.success || !res.data) {
        message.error({ content: 'Failed to retrieve student profile records for export', key: 'exportMsg' });
        return;
      }

      const rows = res.data;
      if (rows.length === 0) {
        message.info({ content: 'No student profiles found to export', key: 'exportMsg' });
        return;
      }

      // Map all 12 sections into human-friendly Excel columns
      const excelData = rows.map((r, index) => ({
        '#': index + 1,
        'Index Number': r.indexNumber ? `STU-${String(r.indexNumber).padStart(5, '0')}` : 'N/A',
        'Account Name': r.accountName || '—',
        'Full Legal Name': r.fullName || r.accountName || '—',
        'Certificate Name': r.certificateName || r.accountName || '—',
        'Certificate Name Confirmed': r.certificateNameConfirmed ? 'Yes' : 'No',
        'Profile Submission Status': r.submissionStatus ? (r.submissionStatus === 'submitted' ? 'Submitted' : r.submissionStatus) : 'Not Started',
        'Date of Birth': r.dateOfBirth ? dayjs(r.dateOfBirth).format('YYYY-MM-DD') : '—',
        'Gender': r.gender || '—',
        'NIC / Passport': r.nicPassport || '—',
        'Profile Photo URL': r.profilePhotoUrl || '—',
        'Primary Contact': r.primaryContact || r.accountPhone || '—',
        'WhatsApp Number': r.whatsappNumber || '—',
        'Email Address': r.email || r.accountEmail || '—',
        'Secondary Contact': r.secondaryContact || '—',
        'Preferred Communication': r.preferredCommunication || 'WhatsApp',
        'Preferred Contact Time': r.preferredContactTime || '—',
        'Permanent Address': r.permanentAddress || r.accountAddress || '—',
        'Current Address': r.currentAddress || '—',
        'City': r.city || '—',
        'Postal Code': r.postalCode || '—',
        'District': r.district || '—',
        'Province': r.province || '—',
        'Highest Education': r.highestEducation || '—',
        'Institute / School': r.instituteName || '—',
        'A/L Stream': r.alStream || '—',
        'Degree / Diploma': r.degreeDiploma || '—',
        'Professional Qualifications': r.professionalQualifications || '—',
        'Currently Following Qualifications': r.currentlyFollowingQualifications || '—',
        'Other Qualifications': r.otherQualifications || '—',
        'Employment Status': r.employmentStatus || '—',
        'Company Name': r.companyName || '—',
        'Job Title': r.jobTitle || '—',
        'Department': r.department || '—',
        'Industry': r.industry || '—',
        'Years of Experience': r.yearsOfExperience || '—',
        'Employment Type': r.employmentType || '—',
        'Work Location': r.workLocation || '—',
        'Current Skills': r.currentSkills || '—',
        'Software / Tools': r.softwareSkills || '—',
        'Interested Areas': r.interestedAreas || '—',
        'Skills to Develop': r.skillsToDevelop || '—',
        'Career Goal': r.careerGoal || '—',
        'Courses Interested In': r.coursesInterestedIn || '—',
        'Preferred Learning Mode': r.preferredLearningMode || '—',
        'Preferred Class Time': r.preferredClassTime || '—',
        'Preferred Course Duration': r.preferredCourseDuration || '—',
        'Preferred Language': r.preferredLanguage || '—',
        'Receive Course Updates': r.receiveCourseUpdates ? 'Yes' : 'No',
        'Receive Special Offers': r.receiveSpecialOffers ? 'Yes' : 'No',
        'Receive WhatsApp Updates': r.receiveWhatsappUpdates ? 'Yes' : 'No',
        'Receive Email Updates': r.receiveEmailUpdates ? 'Yes' : 'No',
        'Preferred Marketing Channel': r.preferredMarketingChannel || 'WhatsApp',
        'NIC Front Document URL': r.docNicFront || '—',
        'NIC Back Document URL': r.docNicBack || '—',
        'Passport Photo URL': r.docPassportPhoto || '—',
        'Educational Cert URL': r.docEducationalCert || '—',
        'Certificate Delivery Method': r.deliveryMethod || '—',
        'Delivery Recipient Name': r.deliveryRecipient || '—',
        'Delivery Contact': r.deliveryContact || '—',
        'Delivery Destination Address': r.deliveryAddress || '—',
        'Delivery Nearest City': r.deliveryCity || '—',
        'Delivery District': r.deliveryDistrict || '—',
        'Delivery Postal Code': r.deliveryPostalCode || '—',
        'Courier Company': r.deliveryCourierCompany || '—',
        'Tracking Number': r.deliveryTrackingNumber || '—',
        'Delivery Status': r.deliveryStatus || '—',
        'Profile Submitted Date': r.submittedAt ? dayjs(r.submittedAt).format('YYYY-MM-DD HH:mm:ss') : 'Not Submitted',
        'Account Registered Date': r.registeredAt ? dayjs(r.registeredAt).format('YYYY-MM-DD HH:mm:ss') : '—',
      }));

      const xlsx = await import('xlsx');
      const FileSaver = await import('file-saver');

      const worksheet = xlsx.utils.json_to_sheet(excelData);

      // Auto-size columns for better readability
      const colWidths = Object.keys(excelData[0]).map((key) => ({
        wch: Math.max(key.length + 3, 16)
      }));
      worksheet['!cols'] = colWidths;

      const workbook = {
        Sheets: { 'Student Profiles': worksheet },
        SheetNames: ['Student Profiles'],
      };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
      });

      const dateStr = dayjs().format('YYYY-MM-DD');
      FileSaver.default.saveAs(dataBlob, `IBAS_Student_Profiles_Export_${dateStr}.xlsx`);
      message.success({ content: `Successfully exported ${excelData.length} student profiles to Excel!`, key: 'exportMsg' });
    } catch (err) {
      console.error('handleExportToExcel error:', err);
      message.error({ content: 'An error occurred while exporting student profiles', key: 'exportMsg' });
    } finally {
      setIsExporting(false);
    }
  };

  // Metrics
  const totalSubmissions = submissions.length;
  const totalStudents = totalSubmissions + incompleteCount;

  // Submissions Columns
  const submissionColumns = [
    {
      title: 'Student Details',
      key: 'student',
      width: 260,
      render: (_, record) => (
        <div className="student-cell-info">
          <Avatar size={40} style={{ backgroundColor: '#2563eb', fontWeight: 'bold', flexShrink: 0 }}>
            {record.fullName ? record.fullName.charAt(0).toUpperCase() : <UserOutlined />}
          </Avatar>
          <div className="student-name-meta">
            <strong className="student-name">{record.fullName || 'Anonymous Student'}</strong>
            <span className="student-index">
              Index: {record.indexNumber || 'N/A'} • NIC: {record.nicPassport || 'N/A'}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Certificate Name',
      dataIndex: 'certificateName',
      key: 'certificateName',
      width: 220,
      render: (val) => (
        <div className="cert-name-cell">
          <SafetyCertificateOutlined style={{ color: '#d97706', marginRight: 6 }} />
          <strong>{val || '—'}</strong>
        </div>
      ),
    },
    {
      title: 'Contact Information',
      key: 'contact',
      width: 220,
      render: (_, record) => (
        <div className="contact-meta-cell">
          <div>{record.email}</div>
          <small>{record.primaryContact || record.whatsappNumber || 'No phone'}</small>
        </div>
      ),
    },
    {
      title: 'Location',
      key: 'location',
      width: 160,
      render: (_, record) => (
        <span>
          {record.city ? `${record.city}, ` : ''}{record.district || 'Sri Lanka'}
        </span>
      ),
    },
    {
      title: 'Documents',
      key: 'documents',
      width: 140,
      align: 'center',
      render: (_, record) => {
        const total = Number(record.totalDocuments || 0);
        return (
          <Badge
            count={`${total} Uploaded`}
            style={{
              backgroundColor: total > 0 ? '#2563eb' : '#94a3b8',
              fontSize: '0.78rem',
            }}
          />
        );
      },
    },
    {
      title: 'Submitted Date',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 160,
      render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : 'Draft'),
    },
    {
      title: 'Status',
      dataIndex: 'submissionStatus',
      key: 'submissionStatus',
      width: 140,
      align: 'center',
      render: (status) => renderStatusBadge(status),
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="middle"
          onClick={() => handleOpenStudentDetails(record.userId)}
        >
          View
        </Button>
      ),
    },
  ];

  // Incomplete Students Columns
  const incompleteColumns = [
    {
      title: 'Student Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Email Address',
      dataIndex: 'email',
      key: 'email',
      width: 220,
    },
    {
      title: 'Contact Number',
      dataIndex: 'contactNumber',
      key: 'contactNumber',
      width: 160,
      render: (val) => val || 'Not provided',
    },
    {
      title: 'Registered Date',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      width: 150,
      render: (val) => (val ? dayjs(val).format('YYYY-MM-DD') : '—'),
    },
    {
      title: 'Profile Status',
      dataIndex: 'profileStatus',
      key: 'profileStatus',
      width: 140,
      align: 'center',
      render: (status) => renderStatusBadge(status),
    },
    {
      title: 'Last Draft Saved',
      dataIndex: 'lastDraftUpdated',
      key: 'lastDraftUpdated',
      width: 160,
      render: (val) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : 'Never saved'),
    },
  ];

  // Certificate Delivery Columns
  const deliveryColumns = [
    {
      title: 'Student & Course',
      key: 'course',
      width: 240,
      render: (_, record) => (
        <div>
          <strong>{record.studentName}</strong>
          <div style={{ color: '#2563eb', fontSize: '0.86rem' }}>{record.courseTitle}</div>
        </div>
      ),
    },
    {
      title: 'Certificate Name',
      dataIndex: 'confirmedCertificateName',
      key: 'confirmedCertificateName',
      width: 200,
      render: (val) => <strong>{val}</strong>,
    },
    {
      title: 'Method',
      dataIndex: 'deliveryMethod',
      key: 'deliveryMethod',
      width: 140,
      align: 'center',
      render: (val) => {
        if (val === 'courier') return <Tag color="blue" icon={<CarOutlined />}>Courier</Tag>;
        if (val === 'collect_from_institute') return <Tag color="green">Institute Collection</Tag>;
        return <Tag color="purple">Digital Only</Tag>;
      },
    },
    {
      title: 'Destination / Contact',
      key: 'dest',
      width: 240,
      render: (_, record) => (
        <div style={{ fontSize: '0.86rem' }}>
          <div>{record.recipientName} ({record.deliveryContact})</div>
          <div style={{ color: '#64748b' }}>{record.deliveryAddress || record.nearestCity || '—'}</div>
        </div>
      ),
    },
    {
      title: 'Delivery Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      align: 'center',
      render: (val) => {
        if (val === 'delivered') return <Tag color="success">Delivered</Tag>;
        if (val === 'dispatched') return <Tag color="processing">Dispatched</Tag>;
        return <Tag color="warning">Pending</Tag>;
      },
    },
    {
      title: 'Tracking Info',
      key: 'tracking',
      width: 170,
      render: (_, record) => (
        <div>
          {record.trackingNumber ? (
            <div>
              <strong>{record.trackingNumber}</strong>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{record.courierCompany || 'Courier'}</div>
            </div>
          ) : (
            <span style={{ color: '#94a3b8' }}>Not assigned</span>
          )}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => {
            setSelectedDeliveryReq(record);
            setDeliveryStatus(record.status || 'pending');
            setDeliveryTrackingNumber(record.trackingNumber || '');
            setDeliveryCourierCompany(record.courierCompany || '');
            setTrackingModalVisible(true);
          }}
        >
          Update Dispatch
        </Button>
      ),
    },
  ];

  return (
    <div className="student-verifications-admin-page">
      {/* Page Header */}
      <div className="page-header-banner">
        <div className="header-titles">
          <h2>Student Profile Submissions & Records</h2>
          <p>
            View student submissions, personal information, certificate details, and export all student data to Excel.
          </p>
        </div>
        <div className="header-actions">
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            size="large"
            loading={isExporting}
            onClick={handleExportToExcel}
            style={{ backgroundColor: '#10b981', borderColor: '#10b981', fontWeight: 600 }}
          >
            Export All to Excel
          </Button>
          <Button
            icon={<ReloadOutlined />}
            size="large"
            onClick={() => { fetchSubmissions(); fetchIncompleteStudents(); fetchDeliveryRequests(); }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="metrics-grid">
        <Card className="metric-card metric-total" bordered={false}>
          <div className="metric-icon-box"><FileDoneOutlined /></div>
          <div>
            <span className="metric-title">Submitted Profiles</span>
            <h3 className="metric-count">{totalSubmissions}</h3>
          </div>
        </Card>

        <Card className="metric-card metric-pending" bordered={false}>
          <div className="metric-icon-box"><ClockCircleOutlined /></div>
          <div>
            <span className="metric-title">Incomplete Profiles</span>
            <h3 className="metric-count">{incompleteCount}</h3>
          </div>
        </Card>

        <Card className="metric-card metric-verified" bordered={false}>
          <div className="metric-icon-box"><UserOutlined /></div>
          <div>
            <span className="metric-title">Total Registered Students</span>
            <h3 className="metric-count">{totalStudents}</h3>
          </div>
        </Card>

        <Card className="metric-card metric-action" bordered={false}>
          <div className="metric-icon-box"><CarOutlined /></div>
          <div>
            <span className="metric-title">Delivery Requests</span>
            <h3 className="metric-count">{deliveryRequests.length}</h3>
          </div>
        </Card>
      </div>

      {/* Main Tabs Container */}
      <Card className="table-container-card" bordered={false}>
        <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k)}>
          {/* Tab 1: Submissions */}
          <TabPane tab={<span>Submissions ({totalSubmissions})</span>} key="submissions">
            {/* Filter and Search Bar */}
            <div className="filter-search-bar">
              <Space wrap size="middle">
                <Select
                  value={statusFilter}
                  onChange={(val) => setStatusFilter(val)}
                  style={{ width: 180 }}
                >
                  <Option value="all">All Statuses</Option>
                  <Option value="submitted">Submitted</Option>
                  <Option value="draft">Draft</Option>
                </Select>

                <Input
                  placeholder="Search name, NIC, email..."
                  prefix={<SearchOutlined />}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onPressEnter={fetchSubmissions}
                  style={{ width: 280 }}
                  allowClear
                />

                <Button type="primary" onClick={fetchSubmissions}>
                  Apply Filter
                </Button>

                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleExportToExcel}
                  loading={isExporting}
                >
                  Export to Excel
                </Button>
              </Space>
            </div>

            <Table
              dataSource={submissions}
              columns={submissionColumns}
              rowKey="profileId"
              loading={isLoading}
              pagination={{ pageSize: 15, showTotal: (t) => `Total ${t} submissions` }}
              className="submissions-table"
              scroll={{ x: 1400 }}
            />
          </TabPane>

          {/* Tab 2: Incomplete Students */}
          <TabPane tab={<span>Incomplete Profiles ({incompleteCount})</span>} key="incomplete">
            <p className="tab-hint">
              List of all registered students whose profile is either in draft mode or has not been started yet.
            </p>
            <Table
              dataSource={incompleteStudents}
              columns={incompleteColumns}
              rowKey="userId"
              pagination={{ pageSize: 15 }}
              scroll={{ x: 1030 }}
            />
          </TabPane>

          {/* Tab 3: Certificate Delivery Requests */}
          <TabPane tab={<span>Certificate Delivery Requests ({deliveryRequests.length})</span>} key="delivery">
            <p className="tab-hint">
              Manage physical certificate dispatch, courier tracking numbers, and delivery confirmation.
            </p>
            <Table
              dataSource={deliveryRequests}
              columns={deliveryColumns}
              rowKey="id"
              pagination={{ pageSize: 15 }}
              scroll={{ x: 1260 }}
            />
          </TabPane>
        </Tabs>
      </Card>

      {/* ─── Detailed Student Profile View Drawer ────────────────────────── */}
      <Drawer
        title={
          <div className="drawer-header-title">
            <UserOutlined style={{ color: '#2563eb', marginRight: 8 }} />
            <span>Student Profile Details</span>
          </div>
        }
        width={850}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        className="student-review-drawer"
      >
        <Spin spinning={profileLoading}>
          {detailedData && (
            <div className="drawer-body-content">
              {/* Highlighted Certificate Name Banner */}
              <div className="cert-name-highlight-banner">
                <div className="banner-tag">OFFICIAL CERTIFICATE NAME</div>
                <h3 className="banner-name">{detailedData.profile?.certificateName || detailedData.user?.name}</h3>
                <span className="banner-status">
                  Confirmation: {detailedData.profile?.certificateNameConfirmed ? ' Confirmed by Student' : ' Not Confirmed'}
                </span>
              </div>

              {/* Status Header Bar */}
              <div className="drawer-status-bar">
                <div>
                  <span className="label">Submission Status: </span>
                  {renderStatusBadge(detailedData.profile?.submissionStatus || 'draft')}
                </div>
                {detailedData.profile?.submittedAt && (
                  <div className="submitted-time">
                    Submitted: {dayjs(detailedData.profile.submittedAt).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                )}
              </div>

              {/* Section 01 & 02: Personal & Contact */}
              <Card title="Section 01 & 02: Personal & Contact Details" size="small" className="section-review-card">
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Full Legal Name" span={2}>
                    <strong>{detailedData.profile?.fullName || detailedData.user?.name}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="NIC / Passport Number">
                    <strong>{detailedData.profile?.nicPassport || '—'}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Date of Birth">
                    {detailedData.profile?.dateOfBirth ? dayjs(detailedData.profile.dateOfBirth).format('YYYY-MM-DD') : '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Gender">
                    {detailedData.profile?.gender || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Primary Contact Number">
                    {detailedData.profile?.primaryContact || detailedData.user?.contactNumber || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="WhatsApp Number">
                    {detailedData.profile?.whatsappNumber || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email Address">
                    {detailedData.profile?.email || detailedData.user?.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Preferred Comm. Method">
                    <Tag color="blue">{detailedData.profile?.preferredCommunication || 'WhatsApp'}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Preferred Contact Time">
                    {detailedData.profile?.preferredContactTime || 'Anytime'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Section 03 & 04: Address & Education */}
              <Card title="Section 03 & 04: Address & Qualifications" size="small" className="section-review-card">
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Permanent Address" span={2}>
                    {detailedData.profile?.permanentAddress || detailedData.user?.address || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Address" span={2}>
                    {detailedData.profile?.currentAddress || 'Same as permanent'}
                  </Descriptions.Item>
                  <Descriptions.Item label="City / Town">
                    {detailedData.profile?.city || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="District / Province">
                    {detailedData.profile?.district}, {detailedData.profile?.province}
                  </Descriptions.Item>
                  <Descriptions.Item label="Postal Code">
                    {detailedData.profile?.postalCode || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Highest Education">
                    <strong>{detailedData.profile?.highestEducation || '—'}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Institute / University" span={2}>
                    {detailedData.profile?.instituteName || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="A/L Stream">
                    {detailedData.profile?.alStream || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Degree / Diploma">
                    {detailedData.profile?.degreeDiploma || '—'}
                  </Descriptions.Item>
                  {detailedData.profile?.professionalQualifications && (
                    <Descriptions.Item label="Professional Qualifications" span={2}>
                      {detailedData.profile.professionalQualifications}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>

              {/* Section 05 & 06: Employment & Skills */}
              <Card title="Section 05 & 06: Employment, Skills & Interests" size="small" className="section-review-card">
                <Descriptions bordered size="small" column={2}>
                  <Descriptions.Item label="Employment Status">
                    <strong>{detailedData.profile?.employmentStatus || '—'}</strong>
                  </Descriptions.Item>
                  <Descriptions.Item label="Company Name">
                    {detailedData.profile?.companyName || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Job Title">
                    {detailedData.profile?.jobTitle || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Industry">
                    {detailedData.profile?.industry || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Years of Experience">
                    {detailedData.profile?.yearsOfExperience || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Work Location">
                    {detailedData.profile?.workLocation || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Current Skills" span={2}>
                    {detailedData.profile?.currentSkills || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Software Skills" span={2}>
                    {detailedData.profile?.softwareSkills || '—'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Career Goal" span={2}>
                    {detailedData.profile?.careerGoal || '—'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              {/* Section 08: Verification Documents */}
              <Card title="Section 08: Uploaded Verification Documents" size="small" className="section-review-card">
                {detailedData.documents && detailedData.documents.length > 0 ? (
                  <div className="documents-review-grid">
                    {detailedData.documents.map((doc) => {
                      const isPdf = doc.fileType === 'application/pdf' || doc.fileUrl?.endsWith('.pdf');
                      return (
                        <div key={doc.id} className="doc-review-item doc-verified">
                          <div className="doc-icon-preview">
                            {isPdf ? <FilePdfOutlined style={{ color: '#ef4444' }} /> : <FileImageOutlined style={{ color: '#3b82f6' }} />}
                          </div>
                          <div className="doc-meta-info">
                            <strong className="doc-type-label">
                              {doc.documentType === 'nic_front' && 'NIC Front'}
                              {doc.documentType === 'nic_back' && 'NIC Back'}
                              {doc.documentType === 'passport_photo' && 'Passport Photo'}
                              {doc.documentType === 'educational_cert' && 'Educational Certificate'}
                              {!['nic_front', 'nic_back', 'passport_photo', 'educational_cert'].includes(doc.documentType) && doc.documentType}
                            </strong>
                            <span className="doc-filename">{doc.fileName}</span>
                            <div className="doc-status-row">
                              {doc.uploadedAt && <span className="doc-date">Uploaded: {dayjs(doc.uploadedAt).format('YYYY-MM-DD HH:mm')}</span>}
                            </div>
                          </div>

                          <div className="doc-actions-col">
                            <Button
                              size="middle"
                              type="primary"
                              ghost
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              View File
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p style={{ color: '#94a3b8' }}>No verification documents uploaded yet.</p>
                )}
              </Card>

              {/* Section 09 & 10: Academic Snapshot & Certificate Delivery */}
              <Card title="Section 09 & 10: Academic Snapshot & Certificate Delivery" size="small" className="section-review-card">
                {detailedData.academicSnapshot && detailedData.academicSnapshot.length > 0 && (
                  <div className="academic-courses-review">
                    <h5 style={{ margin: '0 0 8px', color: '#1e3a8a' }}>Enrolled Programmes</h5>
                    {detailedData.academicSnapshot.map((course, i) => (
                      <div key={i} className="academic-course-row">
                        <div>
                          <strong>{course.courseName}</strong> ({course.courseType})
                        </div>
                        <div>
                          Reg: {course.studentRegNo} • Marks: {course.marks} • Grade: {course.grade}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {detailedData.deliveryRequests && detailedData.deliveryRequests.length > 0 ? (
                  <Descriptions bordered size="small" column={2} style={{ marginTop: 12 }}>
                    <Descriptions.Item label="Delivery Method">
                      <strong>{detailedData.deliveryRequests[0].deliveryMethod}</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Recipient Name">
                      {detailedData.deliveryRequests[0].recipientName}
                    </Descriptions.Item>
                    <Descriptions.Item label="Delivery Contact">
                      {detailedData.deliveryRequests[0].deliveryContact}
                    </Descriptions.Item>
                    <Descriptions.Item label="Destination Address" span={2}>
                      {detailedData.deliveryRequests[0].deliveryAddress || '—'}
                    </Descriptions.Item>
                    <Descriptions.Item label="City / District">
                      {detailedData.deliveryRequests[0].nearestCity}, {detailedData.deliveryRequests[0].district}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tracking Number">
                      {detailedData.deliveryRequests[0].trackingNumber || 'Pending dispatch'}
                    </Descriptions.Item>
                  </Descriptions>
                ) : (
                  <p style={{ color: '#94a3b8', marginTop: 8 }}>No delivery requests confirmed yet.</p>
                )}
              </Card>
            </div>
          )}
        </Spin>
      </Drawer>

      {/* Tracking Modal */}
      <Modal
        title="Update Dispatch & Tracking Details"
        open={trackingModalVisible}
        onOk={handleSaveDeliveryTracking}
        onCancel={() => setTrackingModalVisible(false)}
        okText="Save Tracking"
      >
        <Form layout="vertical">
          <Form.Item label="Delivery Status">
            <Select value={deliveryStatus} onChange={(v) => setDeliveryStatus(v)}>
              <Option value="pending">Pending</Option>
              <Option value="dispatched">Dispatched</Option>
              <Option value="delivered">Delivered</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Courier Company">
            <Input
              value={deliveryCourierCompany}
              onChange={(e) => setDeliveryCourierCompany(e.target.value)}
              placeholder="e.g. PromptX, Domex, Pronto"
            />
          </Form.Item>
          <Form.Item label="Tracking / Waybill Number">
            <Input
              value={deliveryTrackingNumber}
              onChange={(e) => setDeliveryTrackingNumber(e.target.value)}
              placeholder="e.g. PX-98421098"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StudentVerificationsAdmin;
